import { Injectable } from '@angular/core';
import { BaseState } from "./base.state";
import { BehaviorSubject, Observable } from "rxjs";
import { CaseModule, Module } from "src/app/shared/models/module";
import { Attention } from 'src/app/shared/models/attention';
import { Pagination } from "src/app/shared/models/pagination";

export interface MetaDataModules {

}

@Injectable({
    providedIn: 'root',
})

export class ModulesState extends BaseState {

    override store = {
        // Modules
        modules$: new BehaviorSubject<Module[]>([]),
        modulesLight$: new BehaviorSubject<Module[]>([]),
        loadingGetModules$: new BehaviorSubject<boolean>(false),
        loadingGetModulesLight$: new BehaviorSubject<boolean>(false),
        loadingCreatingModule$: new BehaviorSubject<boolean>(false),
        loadingUpdatingModule$: new BehaviorSubject<boolean>(false),

        // Pagination
        pagination$: new BehaviorSubject<Pagination>(null),
        loadingGettingPagination$: new BehaviorSubject<boolean>(false),

        // Metadata
        metaDataModules$: new BehaviorSubject<MetaDataModules>(null),

        // Count
        modulesCount$: new BehaviorSubject<number>(0),

        //CaseModules
        caseModules$: new BehaviorSubject<CaseModule[]>([]),
        loadingGettingCaseModules$: new BehaviorSubject<boolean>(false),
        loadingCreatingCaseModule$: new BehaviorSubject<boolean>(false),
        loadingUpdatingCaseModule$: new BehaviorSubject<boolean>(false),
        caseModulesCount$: new BehaviorSubject<number>(0),

        //ModuleAttentions
        moduleAttentions$: new BehaviorSubject<Attention[]>([]),
        loadingGettingModuleAttentions$: new BehaviorSubject<boolean>(false),
        loadingCreatingModuleAttention$: new BehaviorSubject<boolean>(false),
        loadingUpdatingModuleAttention$: new BehaviorSubject<boolean>(false),
        moduleAttentionsCount$: new BehaviorSubject<number>(0),

    };

    constructor() {
        super();
    }

    getModules$(): Observable<Module[]> {
        return this.store.modules$.asObservable();
    }

    getModules(): Module[] {
        const current = this.store.modules$.getValue();
        return Array.isArray(current) ? current : [];
    };

    getCaseModules$(): Observable<CaseModule[]> {
        return this.store.caseModules$.asObservable();
    }

    setModules(modules: Module[]) {
        this.store.modules$.next(modules);
    }

    setCaseModules(caseModules: CaseModule[]) {
        this.store.caseModules$.next(caseModules);
    }

    getCaseModulesValue(): CaseModule[] {
        const current = this.store.caseModules$.getValue();
        return Array.isArray(current) ? current : [];
    }

    upsertCaseModule(caseModule: CaseModule): void {
        if (!caseModule) return;
        const list = this.getCaseModulesValue();
        const id = Number((caseModule as any)?.id);
        if (!Number.isFinite(id)) return;

        const idx = list.findIndex(cm => Number((cm as any)?.id) === id);
        if (idx >= 0) {
            const next = [...list];
            next[idx] = { ...(next[idx] as any), ...(caseModule as any) };
            this.setCaseModules(next);
            return;
        }

        this.setCaseModules([...list, caseModule]);
    }

    addModule(module: Module) {
        const currentModules = this.store.modules$.getValue();
        this.store.modules$.next([...currentModules, module]);
    }

    addCaseModule(caseModule: CaseModule) {
        const currentCaseModules = this.store.caseModules$.getValue();
        this.store.caseModules$.next([...currentCaseModules, caseModule]);
    }

    deleteCaseModule(caseModuleId: number) {
        const currentCaseModules = this.store.caseModules$.getValue();
        const nextCaseModules = currentCaseModules.filter(cm => Number((cm as any)?.id) !== Number(caseModuleId));
        this.store.caseModules$.next(nextCaseModules);
    }

    isLoadingGetModules$(): Observable<boolean> {
        return this.store.loadingGetModules$.asObservable();
    }

    isLoadingGettingCaseModules$(): Observable<boolean> {
        return this.store.loadingGettingCaseModules$.asObservable();
    }

    setLoadingGetModules(isLoading: boolean) {
        this.store.loadingGetModules$.next(isLoading);
    }

    setLoadingGettingCaseModules(isLoading: boolean) {
        this.store.loadingGettingCaseModules$.next(isLoading);
    }

    isLoadingCreatingModule$(): Observable<boolean> {
        return this.store.loadingCreatingModule$.asObservable();
    }

    setLoadingUpdatingModule(isLoading: boolean) {
        this.store.loadingUpdatingModule$.next(isLoading);
    }

    isLoadingUpdatingModule$(): Observable<boolean> {
        return this.store.loadingUpdatingModule$.asObservable();
    }

    isLoadingCreatingCaseModule$(): Observable<boolean> {
        return this.store.loadingCreatingCaseModule$.asObservable();
    }

    setLoadingCreatingModule(isLoading: boolean) {
        this.store.loadingCreatingModule$.next(isLoading);
    }

    setLoadingCreatingCaseModule(isLoading: boolean) {
        this.store.loadingCreatingCaseModule$.next(isLoading);
    }

    isLoadingUpdatingCaseModule$(): Observable<boolean> {
        return this.store.loadingUpdatingCaseModule$.asObservable();
    }

    setLoadingUpdatingCaseModule(isLoading: boolean) {
        this.store.loadingUpdatingCaseModule$.next(isLoading);
    }

    getModulesLight$(): Observable<Module[]> {
        return this.store.modulesLight$.asObservable();
    }

    setModulesLight(modules: Module[]) {
        this.store.modulesLight$.next(modules);
    }

    isLoadingGetModulesLight$(): Observable<boolean> {
        return this.store.loadingGetModulesLight$.asObservable();
    }

    setLoadingGetModulesLight(isLoading: boolean) {
        this.store.loadingGetModulesLight$.next(isLoading);
    }

    getModulesCount$(): Observable<number> {
        return this.store.modulesCount$.asObservable();
    }

    setModulesCount(count: number) {
        this.store.modulesCount$.next(count);
    }

    getModuleAttentions$(): Observable<Attention[]> {
        return this.store.moduleAttentions$.asObservable();
    };

    getModuleAttentions(): Attention[] {
        const current = this.store.moduleAttentions$.getValue();
        return Array.isArray(current) ? current : [];
    }

    setModuleAttentions(attentions: Attention[]) {
        this.store.moduleAttentions$.next(attentions);
    }

    isLoadingGettingModuleAttentions$(): Observable<boolean> {
        return this.store.loadingGettingModuleAttentions$.asObservable();
    };

    setLoadingGettingModuleAttentions(isLoading: boolean) {
        this.store.loadingGettingModuleAttentions$.next(isLoading);
    };

    getModuleAttentionsCount$(): Observable<number> {
        return this.store.moduleAttentionsCount$.asObservable();
    };

    setModuleAttentionsCount(count: number) {
        this.store.moduleAttentionsCount$.next(count);
    };

    resetCaseScopedState(): void {
        // Keep module catalog intact; clear only data tied to the currently opened case.
        this.store.caseModules$.next([]);
        this.store.caseModulesCount$.next(0);
        this.store.loadingGettingCaseModules$.next(false);
        this.store.loadingCreatingCaseModule$.next(false);
        this.store.loadingUpdatingCaseModule$.next(false);

        this.store.moduleAttentions$.next([]);
        this.store.moduleAttentionsCount$.next(0);
        this.store.loadingGettingModuleAttentions$.next(false);
        this.store.loadingCreatingModuleAttention$.next(false);
        this.store.loadingUpdatingModuleAttention$.next(false);
    }

}
